import type { LabResultFlag, LabResultLine } from "@/core/laboratory/types";
import {
  createLabResultLineFromTemplate,
  LAB_RESULT_FLAG_LABELS,
  labResultPresetForTest,
} from "@/core/laboratory/types";

export type LabResultLineDraft = LabResultLine;

export function linesToDraft(lines: LabResultLine[]): LabResultLineDraft[] {
  return lines.map((l) => ({ ...l }));
}

export function draftFromTestName(testName: string): LabResultLineDraft[] {
  return labResultPresetForTest(testName).map((t, i) => createLabResultLineFromTemplate(t, i));
}

export function emptyResultLine(index: number): LabResultLineDraft {
  return createLabResultLineFromTemplate(
    { parameter: "", unit: "", referenceRange: "" },
    index,
  );
}

interface LabResultLinesEditorProps {
  lines: LabResultLineDraft[];
  testName: string;
  readOnly?: boolean;
  comment: string;
  onLinesChange: (lines: LabResultLineDraft[]) => void;
  onCommentChange: (comment: string) => void;
}

export function LabResultLinesEditor({
  lines,
  testName,
  readOnly = false,
  comment,
  onLinesChange,
  onCommentChange,
}: LabResultLinesEditorProps) {
  const updateLine = (id: string, patch: Partial<LabResultLineDraft>) => {
    onLinesChange(lines.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const addLine = () => {
    onLinesChange([...lines, emptyResultLine(lines.length)]);
  };

  const removeLine = (id: string) => {
    if (lines.length <= 1) return;
    onLinesChange(lines.filter((l) => l.id !== id));
  };

  const loadPreset = () => {
    onLinesChange(draftFromTestName(testName));
  };

  return (
    <div>
      {!readOnly && (
        <div className="d-flex flex-wrap gap-2 mb-2 ph-split-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={loadPreset}>
            Charger modèle ({testName.slice(0, 24)}
            {testName.length > 24 ? "…" : ""})
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addLine}>
            + Ligne
          </button>
        </div>
      )}

      <div className="table-responsive ph-diag-table">
        <table className="table table-sm ph-dash-table align-middle mb-2">
          <thead>
            <tr>
              <th>Paramètre</th>
              <th>Valeur</th>
              <th>Unité</th>
              <th>Norme</th>
              <th>Flag</th>
              {!readOnly && <th className="text-end" />}
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.id} className="ph-diag-row">
                <td data-label="Paramètre">
                  {readOnly ? (
                    line.parameter || "—"
                  ) : (
                    <input
                      className="form-control form-control-sm"
                      value={line.parameter}
                      onChange={(e) => updateLine(line.id, { parameter: e.target.value })}
                    />
                  )}
                </td>
                <td data-label="Valeur">
                  {readOnly ? (
                    <span className={flagClass(line.flag)}>{line.value || "—"}</span>
                  ) : (
                    <input
                      className="form-control form-control-sm"
                      value={line.value}
                      onChange={(e) => updateLine(line.id, { value: e.target.value })}
                    />
                  )}
                </td>
                <td className="small" data-label="Unité">
                  {readOnly ? (
                    line.unit || "—"
                  ) : (
                    <input
                      className="form-control form-control-sm"
                      value={line.unit}
                      onChange={(e) => updateLine(line.id, { unit: e.target.value })}
                    />
                  )}
                </td>
                <td className="small" data-label="Norme">
                  {readOnly ? (
                    line.referenceRange || "—"
                  ) : (
                    <input
                      className="form-control form-control-sm"
                      value={line.referenceRange}
                      onChange={(e) => updateLine(line.id, { referenceRange: e.target.value })}
                    />
                  )}
                </td>
                <td data-label="Flag">
                  {readOnly ? (
                    line.flag ? LAB_RESULT_FLAG_LABELS[line.flag] : "—"
                  ) : (
                    <select
                      className="form-select form-select-sm"
                      value={line.flag}
                      onChange={(e) =>
                        updateLine(line.id, { flag: e.target.value as LabResultFlag })
                      }
                    >
                      {(Object.keys(LAB_RESULT_FLAG_LABELS) as LabResultFlag[]).map((f) => (
                        <option key={f || "none"} value={f}>
                          {LAB_RESULT_FLAG_LABELS[f]}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                {!readOnly && (
                  <td className="text-end ph-diag-no-label" data-label="">
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger p-0"
                      disabled={lines.length <= 1}
                      onClick={() => removeLine(line.id)}
                    >
                      Retirer
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <label className="form-label small fw-semibold" htmlFor="lab-result-comment">
        Commentaire / conclusion
      </label>
      {readOnly ? (
        <p className="small mb-0 text-muted" style={{ whiteSpace: "pre-wrap" }}>
          {comment || "—"}
        </p>
      ) : (
        <textarea
          id="lab-result-comment"
          className="form-control form-control-sm"
          rows={3}
          placeholder="Interprétation globale, recommandations…"
          value={comment}
          onChange={(e) => onCommentChange(e.target.value)}
        />
      )}
    </div>
  );
}

function flagClass(flag: LabResultFlag): string {
  if (flag === "high" || flag === "critical") return "text-danger fw-semibold";
  if (flag === "low") return "text-warning fw-semibold";
  return "";
}

export function LabResultLinesReadTable({ lines }: { lines: LabResultLine[] }) {
  if (lines.length === 0) return null;
  return (
    <div className="table-responsive ph-diag-table">
      <table className="table table-sm ph-dash-table mb-0">
        <thead>
          <tr>
            <th>Paramètre</th>
            <th>Résultat</th>
            <th>Unité</th>
            <th>Norme</th>
            <th>Flag</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id} className="ph-diag-row">
              <td data-label="Paramètre">{line.parameter}</td>
              <td className={flagClass(line.flag)} data-label="Résultat">
                {line.value}
              </td>
              <td className="small" data-label="Unité">
                {line.unit || "—"}
              </td>
              <td className="small text-muted" data-label="Norme">
                {line.referenceRange || "—"}
              </td>
              <td className="small" data-label="Flag">
                {line.flag ? LAB_RESULT_FLAG_LABELS[line.flag] : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
